/**
 * @format
 * @module ui/templates/audio
 */
import tgamConfig from "config/tgam";

const prevButton = `
  <button type="button" class="plyr__control c-button c-button--icon-only c-button--no-border c-button--no-border-radius c-audio-player__previous-button" aria-label="Previous" data-plyr="previous">
    <svg class="c-button__icon c-audio-player__controls__previous-icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
      <use xlink:href="${tgamConfig.env.svgSpritePath}#audio-track-control"></use>
    </svg>
  </button>
`;

const nextButton = `
  <button type="button" class="plyr__control c-button c-button--icon-only c-button--no-border c-button--no-border-radius c-audio-player__next-button" aria-label="Next" data-plyr="next">
    <svg class="c-button__icon c-audio-player__controls__next-icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
      <use xlink:href="${tgamConfig.env.svgSpritePath}#audio-track-control"></use>
    </svg>
  </button>
`;

function playButton(includePrevNext) {
  const marginClass = includePrevNext ? "" : "c-audio-player__play-button--extra-margin";
  return `
    <button type="button" class="plyr__control c-button c-button--icon-only c-button--no-border c-button--no-border-radius c-audio-player__play-button ${marginClass}" aria-label="Play" data-plyr="play">
      <svg class="c-button__icon icon--pressed c-audio-player__controls__play-icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
        <use xlink:href="${tgamConfig.env.svgSpritePath}#audio-pause-circle" class="c-audio-player__controls__player-icon-use"></use>
      </svg>
      <svg class="c-button__icon icon--not-pressed c-audio-player__controls__play-icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
        <use xlink:href="${tgamConfig.env.svgSpritePath}#audio-play-circle" class="c-audio-player__controls__player-icon-use"></use>
      </svg>
    </button>
  `;
}

const progressBar = `
  <div class="plyr__progress c-audio-player__progress">
    <input class="c-audio-player__timeline" data-plyr="seek" type="range" min="0" max="100" step="0.01" value="0"
        aria-label="Seek">
    <progress class="plyr__progress__buffer" min="0" max="100" value="0">% buffered</progress>
  </div>
  <div class="plyr__time plyr__time--current c-audio-player__time" aria-label="Current time">00:00</div>
`;

const closeButton = `
  <button type="button" class="plyr__control c-button c-button--icon-only c-button--no-border c-button--no-border-radius c-audio-player__dismiss js-audio-btn-close" aria-label="Close audio player">
    <svg class="c-button__icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
      <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
    </svg>
  </button>
`;

/**
 * Generate the HTML for the audio player controls
 * @param {Object} [c={}] - collection of params
 * @param {String} [c.idPrefix="arc-audio-player-1"] - id attribute namespace
 * @param {Boolean} [c.includePrevNext=false] - display previous and next buttons
 * @param {Boolean} [c.displayCloseBtn=true] - display close button
 * @returns {String} of HTML
 */
export function audioPlayerControlsMarkup({
  idPrefix = "arc-audio-player-1",
  includePrevNext = false,
  displayCloseBtn = true
} = {}) {
  return `
    <div class="c-audio-player__controls plyr__controls c-audio-player__controls--page">
      ${includePrevNext ? prevButton : ""}
      ${playButton(includePrevNext)}
      ${includePrevNext ? nextButton : ""}
      ${progressBar}

      <div class="c-audio-player__controls__settings">
        <button type="button"
          class="plyr__control c-button c-button--icon-only c-button--no-border c-button--no-border-radius c-audio-player__settings-button js-dropdown-menu-trigger js-audio-settings"
          id="${idPrefix}-voice-trigger"
          aria-controls="${idPrefix}-voice-dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          data-plyr="custom-settings">
          <span class="c-button__text">Voice</span>
        </button>

        <div class="c-dropdown-menu c-dropdown-menu--pos-bottom c-dropdown-menu--pos-right js-audio-settings-dropdown"
          id="${idPrefix}-voice-dropdown"
          aria-labelledby="${idPrefix}-voice-trigger"
          role="menu" hidden>

          <div class="c-dropdown-menu__inner">
            <fieldset class="c-form__fieldset c-form__fieldset--radios">
              <legend class="c-form__legend">Voice settings</legend>

              <div class="js-c-audio-player-languages c-form__field c-form__field--radios">
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-en-f" class="c-form__control c-form__control--radio" name="language" type="radio"
                    value="en_F">
                  <label for="${idPrefix}-en-f" class="c-form__label">English Female</label>
                </div>
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-en-m" class="c-form__control c-form__control--radio" name="language" type="radio"
                    value="en_M">
                  <label for="${idPrefix}-en-m" class="c-form__label">English Male</label>
                </div>
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-fr-f" class="c-form__control c-form__control--radio" name="language" type="radio"
                    value="fr_F">
                  <label for="${idPrefix}-fr-f" class="c-form__label">French Female</label>
                </div>
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-fr-m" class="c-form__control c-form__control--radio" name="language" type="radio"
                    value="fr_M">
                  <label for="${idPrefix}-fr-m" class="c-form__label">French Male</label>
                </div>
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-cn-f" class="c-form__control c-form__control--radio" name="language" type="radio"
                    value="cn_F">
                  <label for="${idPrefix}-cn-f" class="c-form__label">Mandarin Female</label>
                </div>
              </div>

              <button type="button" class="c-button c-button--full-width c-button--no-border-radius c-dropdown-menu__close js-dropdown-menu-close">
                <span class="c-button__text">Close<span class="u-visually-hidden"> voice settings menu</span></span>
              </button>
            </fieldset>
          </div>
        </div>
      </div>

      <div class="c-audio-player__controls__speed">
        <button type="button"
          class="plyr__control c-button c-button--icon-only c-button--no-border c-button--no-border-radius c-audio-player__speed-button js-dropdown-menu-trigger js-audio-speed"
          id="${idPrefix}-speed-trigger"
          aria-controls="${idPrefix}-speed-dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          aria-label="Speed"
          data-plyr="speed">
          <span class="c-button__text">1x</span>
        </button>

        <div class="c-dropdown-menu c-dropdown-menu--pos-bottom c-dropdown-menu--pos-right js-audio-speed-dropdown"
          id="${idPrefix}-speed-dropdown"
          aria-labelledby="${idPrefix}-speed-trigger"
          role="menu" hidden>

          <div class="c-dropdown-menu__inner">
            <fieldset class="c-form__fieldset c-form__fieldset--radios">
              <legend class="c-form__legend">Playback speed</legend>

              <div class="js-c-audio-player-speed c-form__field c-form__field--radios">
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-1x" class="c-form__control c-form__control--radio" name="speed" type="radio"
                      value="1">
                  <label for="${idPrefix}-1x" class="c-form__label">1x</label>
                </div>
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-1-25x" class="c-form__control c-form__control--radio" name="speed" type="radio"
                      value="1.25">
                  <label for="${idPrefix}-1-25x" class="c-form__label">1.25x</label>
                </div>
                <div class="c-form__field c-form__field--radio">
                  <input id="${idPrefix}-1-5x" class="c-form__control c-form__control--radio" name="speed" type="radio"
                      value="1.5">
                  <label for="${idPrefix}-1-5x" class="c-form__label">1.5x</label>
                </div>
              </div>

              <button type="button" class="c-button c-button--full-width c-button--no-border-radius c-dropdown-menu__close js-dropdown-menu-close">
                <span class="c-button__text">Close<span class="u-visually-hidden"> playback speed settings menu</span></span>
              </button>
            </fieldset>
          </div>
        </div>
      </div>

      ${displayCloseBtn ? closeButton : ""}
    </div>
  `;
}
